import * as THREE from "three";
import type {
  FrameContext,
  MovementResolutionRequest,
  PlayerSnapshot,
  RuntimeSystem,
  WorldQuery,
} from "../core/types";
import { HOME_PITCH, HOME_POSITION } from "../core/types";
import { hasTouchInput } from "../core/device";
import type {
  InteractionRay,
  InteractionTarget,
  PlayerInteractionHooks,
  PlayerMovementOptions,
  TouchMovementInput,
} from "./types";

export interface PlayerControllerOptions<T = unknown> extends PlayerMovementOptions {
  camera: THREE.PerspectiveCamera;
  domElement: HTMLElement;
  world: WorldQuery;
  homePosition?: readonly [number, number, number];
  interaction?: PlayerInteractionHooks<T>;
  onPausedChange?: (paused: boolean) => void;
  reducedMotion?: boolean;
}

const UP = new THREE.Vector3(0, 1, 0);
const DEFAULTS = {
  walkSpeed: 2.25,
  runSpeed: 3.55,
  crouchSpeed: 1.25,
  collisionRadius: 0.32,
  standingHeight: 1.7,
  crouchingHeight: 1.18,
  autoStepHeight: 0.48,
  maxWadeDepth: 0.68,
  lookSensitivity: 0.0018,
  interactionDistance: 2.4,
};

type ResolvedMovementOptions = Required<PlayerMovementOptions>;

export class PlayerController<T = unknown> implements RuntimeSystem {
  readonly camera: THREE.PerspectiveCamera;

  private readonly domElement: HTMLElement;
  private readonly world: WorldQuery;
  private readonly homePosition: readonly [number, number, number];
  private readonly options: ResolvedMovementOptions;
  private readonly keys = new Set<string>();
  private readonly velocity = new THREE.Vector3();
  private readonly desiredVelocity = new THREE.Vector3();
  private readonly forward = new THREE.Vector3();
  private readonly right = new THREE.Vector3();
  private readonly candidate = new THREE.Vector3();
  private readonly movementRequest: MovementResolutionRequest = {
    position: { x: 0, z: 0 },
    displacement: { x: 0, z: 0 },
    radius: DEFAULTS.collisionRadius,
    maxStepHeight: DEFAULTS.autoStepHeight,
    maxWadeDepth: DEFAULTS.maxWadeDepth,
  };
  private readonly interactionOrigin = new THREE.Vector3();
  private readonly interactionDirection = new THREE.Vector3();
  private readonly interactionRay: InteractionRay;
  private readonly reducedMotion: boolean;
  private readonly onPausedChange?: (paused: boolean) => void;
  private interaction: PlayerInteractionHooks<T>;
  private currentTarget: InteractionTarget<T> | null = null;
  private baseY: number;
  private eyeHeight = DEFAULTS.standingHeight;
  private yaw = 0;
  private pitch = 0;
  private stride = 0;
  private paused = true;
  private seated = false;
  private touchInput: TouchMovementInput = { x: 0, z: 0, running: false };
  private touchCrouching = false;
  private disposed = false;

  constructor(options: PlayerControllerOptions<T>) {
    this.camera = options.camera;
    this.domElement = options.domElement;
    this.world = options.world;
    this.homePosition = options.homePosition ?? HOME_POSITION;
    this.options = {
      walkSpeed: options.walkSpeed ?? DEFAULTS.walkSpeed,
      runSpeed: options.runSpeed ?? DEFAULTS.runSpeed,
      crouchSpeed: options.crouchSpeed ?? DEFAULTS.crouchSpeed,
      collisionRadius: options.collisionRadius ?? DEFAULTS.collisionRadius,
      standingHeight: options.standingHeight ?? DEFAULTS.standingHeight,
      crouchingHeight: options.crouchingHeight ?? DEFAULTS.crouchingHeight,
      autoStepHeight: options.autoStepHeight ?? DEFAULTS.autoStepHeight,
      maxWadeDepth: options.maxWadeDepth ?? DEFAULTS.maxWadeDepth,
      lookSensitivity: options.lookSensitivity ?? DEFAULTS.lookSensitivity,
      interactionDistance:
        options.interactionDistance ?? DEFAULTS.interactionDistance,
    };
    this.interaction = options.interaction ?? {};
    this.onPausedChange = options.onPausedChange;
    this.baseY = this.camera.position.y;
    this.eyeHeight = this.options.standingHeight;
    this.reducedMotion =
      options.reducedMotion ??
      globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ??
      false;
    this.interactionRay = {
      origin: this.interactionOrigin,
      direction: this.interactionDirection,
      maxDistance: this.options.interactionDistance,
    };

    this.camera.rotation.order = "YXZ";
    this.readCameraRotation();
    document.addEventListener("pointerlockchange", this.handlePointerLockChange);
    document.addEventListener("mousemove", this.handleMouseMove);
    window.addEventListener("keydown", this.handleKeyDown);
    window.addEventListener("keyup", this.handleKeyUp);
    window.addEventListener("blur", this.handleBlur);
    this.domElement.addEventListener("click", this.handleSceneClick);
  }

  get isPaused(): boolean {
    return this.paused;
  }

  get hasPointerLock(): boolean {
    return document.pointerLockElement === this.domElement;
  }

  setInteractionHooks(hooks: PlayerInteractionHooks<T>): void {
    this.interaction = hooks;
    this.setCurrentTarget(null);
  }

  async resume(): Promise<boolean> {
    if (this.disposed) return false;
    this.setPaused(false, false);
    if (!this.requiresPointerLock()) return true;
    try {
      await this.domElement.requestPointerLock();
      return this.hasPointerLock;
    } catch {
      this.setPaused(true, false);
      return false;
    }
  }

  setTouchMovement(input: TouchMovementInput): void {
    this.touchInput = {
      x: THREE.MathUtils.clamp(input.x, -1, 1),
      z: THREE.MathUtils.clamp(input.z, -1, 1),
      running: Boolean(input.running),
    };
  }

  addTouchLook(deltaX: number, deltaY: number): void {
    if (this.paused) return;
    this.yaw -= deltaX * this.options.lookSensitivity * 1.15;
    this.pitch = THREE.MathUtils.clamp(
      this.pitch - deltaY * this.options.lookSensitivity * 1.15,
      -Math.PI * 0.48,
      Math.PI * 0.48,
    );
    this.applyCameraRotation();
  }

  setTouchCrouching(crouching: boolean): void {
    this.touchCrouching = crouching;
  }

  interact(): void {
    if (!this.paused && this.currentTarget) this.interaction.onInteract?.(this.currentTarget);
  }

  pause(): void {
    this.setPaused(true, true);
  }

  setPaused(paused: boolean, releasePointerLock = paused): void {
    if (this.paused === paused) {
      if (releasePointerLock && this.hasPointerLock) document.exitPointerLock();
      return;
    }

    this.paused = paused;
    this.keys.clear();
    this.touchInput = { x: 0, z: 0, running: false };
    this.velocity.set(0, 0, 0);
    if (paused) this.setCurrentTarget(null);
    if (releasePointerLock && this.hasPointerLock) document.exitPointerLock();
    this.onPausedChange?.(paused);
  }

  returnHome(): void {
    this.seated = false;
    this.camera.position.fromArray(this.homePosition);
    this.baseY = this.homePosition[1];
    this.yaw = 0;
    this.pitch = HOME_PITCH;
    this.stride = 0;
    this.velocity.set(0, 0, 0);
    this.applyCameraRotation();
    this.world.resetAround(this.camera.position);
  }

  snapshot(): PlayerSnapshot {
    return {
      position: [this.camera.position.x, this.baseY, this.camera.position.z],
      yaw: this.yaw,
      pitch: this.pitch,
    };
  }

  restore(snapshot: PlayerSnapshot): void {
    const [x, y, z] = snapshot.position;
    if (![x, y, z, snapshot.yaw, snapshot.pitch].every(Number.isFinite)) return;

    this.camera.position.set(x, y, z);
    this.baseY = y;
    this.yaw = snapshot.yaw;
    this.pitch = THREE.MathUtils.clamp(snapshot.pitch, -Math.PI * 0.48, Math.PI * 0.48);
    this.stride = 0;
    this.seated = false;
    this.velocity.set(0, 0, 0);
    this.applyCameraRotation();
    this.world.resetAround(this.camera.position);
  }

  update(context: FrameContext): void {
    if (this.disposed || this.paused || context.paused) return;
    const delta = Math.min(Math.max(context.delta, 0), 0.05);
    this.updateMovement(delta, context.settings.comfort.headBob);
    this.updateInteractionTarget();
  }

  sitAt(x: number, z: number): void {
    this.seated = true;
    this.velocity.set(0, 0, 0);
    this.camera.position.x = x;
    this.camera.position.z = z;
    this.eyeHeight = this.options.crouchingHeight;
    this.baseY = this.world.getHeight(x, z) + this.eyeHeight;
    this.camera.position.y = this.baseY;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.pause();
    document.removeEventListener("pointerlockchange", this.handlePointerLockChange);
    document.removeEventListener("mousemove", this.handleMouseMove);
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("keyup", this.handleKeyUp);
    window.removeEventListener("blur", this.handleBlur);
    this.domElement.removeEventListener("click", this.handleSceneClick);
  }

  private updateMovement(delta: number, headBobEnabled: boolean): void {
    const hasMovementInput = ["KeyW", "KeyA", "KeyS", "KeyD"].some((key) => this.keys.has(key)) || Math.hypot(this.touchInput.x, this.touchInput.z) > 0.05;
    if (this.seated && !hasMovementInput) {
      this.camera.position.y = this.baseY;
      return;
    }
    if (hasMovementInput) this.seated = false;
    const crouching = this.touchCrouching || this.keys.has("ControlLeft") || this.keys.has("ControlRight") || this.keys.has("KeyC");
    const running = !crouching && (this.touchInput.running || this.keys.has("ShiftLeft") || this.keys.has("ShiftRight"));
    const targetEyeHeight = crouching
      ? this.options.crouchingHeight
      : this.options.standingHeight;
    this.eyeHeight = THREE.MathUtils.damp(this.eyeHeight, targetEyeHeight, 12, delta);

    const keyboardX = Number(this.keys.has("KeyD")) - Number(this.keys.has("KeyA"));
    const keyboardZ = Number(this.keys.has("KeyW")) - Number(this.keys.has("KeyS"));
    const inputX = THREE.MathUtils.clamp(keyboardX + this.touchInput.x, -1, 1);
    const inputZ = THREE.MathUtils.clamp(keyboardZ + this.touchInput.z, -1, 1);
    const inputLength = Math.min(1, Math.hypot(inputX, inputZ));
    const waterDepth = Math.max(
      0,
      this.world.getWaterDepth(this.camera.position.x, this.camera.position.z),
    );
    const waterSlowdown = THREE.MathUtils.lerp(
      1,
      0.42,
      THREE.MathUtils.clamp(waterDepth / this.options.maxWadeDepth, 0, 1),
    );
    const speed =
      (crouching
        ? this.options.crouchSpeed
        : running
          ? this.options.runSpeed
          : this.options.walkSpeed) * waterSlowdown;

    this.desiredVelocity.set(0, 0, 0);
    if (inputLength > 0) {
      this.forward.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
      this.right.set(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
      this.desiredVelocity
        .addScaledVector(this.forward, inputZ / inputLength)
        .addScaledVector(this.right, inputX / inputLength)
        .multiplyScalar(speed);
    }

    this.velocity.x = THREE.MathUtils.damp(
      this.velocity.x,
      this.desiredVelocity.x,
      inputLength > 0 ? 13 : 18,
      delta,
    );
    this.velocity.z = THREE.MathUtils.damp(
      this.velocity.z,
      this.desiredVelocity.z,
      inputLength > 0 ? 13 : 18,
      delta,
    );

    const startX = this.camera.position.x;
    const startZ = this.camera.position.z;
    const moveX = this.velocity.x * delta;
    const moveZ = this.velocity.z * delta;
    const resolveMovement = this.world.resolveMovement;

    if (resolveMovement) {
      const request = this.movementRequest;
      request.position.x = startX;
      request.position.z = startZ;
      request.displacement.x = moveX;
      request.displacement.z = moveZ;
      request.radius = this.options.collisionRadius;
      request.maxStepHeight = this.options.autoStepHeight;
      request.maxWadeDepth = this.options.maxWadeDepth;
      const resolved = resolveMovement.call(this.world, request);
      this.camera.position.x = resolved.position.x;
      this.camera.position.z = resolved.position.z;
      this.velocity.x = delta > 0 ? resolved.displacement.x / delta : 0;
      this.velocity.z = delta > 0 ? resolved.displacement.z / delta : 0;
    } else {
      this.candidate.copy(this.camera.position);
      this.candidate.x += moveX;
      this.candidate.z += moveZ;
      const currentGround = this.world.getHeight(startX, startZ);
      const nextGround = this.world.getHeight(this.candidate.x, this.candidate.z);
      const nextWaterDepth = Math.max(
        0,
        this.world.getWaterDepth(this.candidate.x, this.candidate.z),
      );
      const leavingWater = waterDepth > 0 && nextWaterDepth < waterDepth;
      const canAdvance =
        (nextGround - currentGround <= this.options.autoStepHeight || leavingWater) &&
        nextWaterDepth <= this.options.maxWadeDepth;

      if (canAdvance) {
        this.camera.position.x = this.candidate.x;
        this.camera.position.z = this.candidate.z;
      } else {
        this.velocity.set(0, 0, 0);
      }
    }

    const groundedY =
      this.world.getHeight(this.camera.position.x, this.camera.position.z) +
      this.eyeHeight;
    this.baseY = THREE.MathUtils.damp(this.baseY, groundedY, 18, delta);
    const horizontalSpeed = Math.hypot(this.velocity.x, this.velocity.z);
    const moving = inputLength > 0 && horizontalSpeed > 0.08;
    this.stride += moving ? horizontalSpeed * delta * 2.15 : 0;
    const bobStrength =
      headBobEnabled && !this.reducedMotion && moving
        ? Math.min(horizontalSpeed / this.options.walkSpeed, 1.35)
        : 0;
    const bob = Math.sin(this.stride * Math.PI) * 0.018 * bobStrength;
    this.camera.position.y = this.baseY + bob;
  }

  private updateInteractionTarget(): void {
    const resolver = this.interaction.resolveTarget;
    if (!resolver) {
      this.setCurrentTarget(null);
      return;
    }

    this.interactionOrigin.copy(this.camera.position);
    this.camera.getWorldDirection(this.interactionDirection).normalize();
    const target = resolver(this.interactionRay);
    this.setCurrentTarget(
      target && target.distance <= this.options.interactionDistance ? target : null,
    );
  }

  private setCurrentTarget(target: InteractionTarget<T> | null): void {
    const changed =
      this.currentTarget?.id !== target?.id ||
      this.currentTarget?.label !== target?.label;
    this.currentTarget = target;
    if (changed) this.interaction.onTargetChange?.(target);
  }

  private readCameraRotation(): void {
    this.yaw = this.camera.rotation.y;
    this.pitch = this.camera.rotation.x;
    this.applyCameraRotation();
  }

  private applyCameraRotation(): void {
    this.camera.rotation.set(this.pitch, this.yaw, 0, "YXZ");
    this.camera.up.copy(UP);
  }

  private readonly handlePointerLockChange = (): void => {
    if (this.hasPointerLock) {
      this.setPaused(false, false);
    } else if (!this.paused) {
      this.setPaused(true, false);
    }
  };

  private readonly handleMouseMove = (event: MouseEvent): void => {
    if (!this.hasPointerLock || this.paused) return;
    this.yaw -= event.movementX * this.options.lookSensitivity;
    this.pitch = THREE.MathUtils.clamp(
      this.pitch - event.movementY * this.options.lookSensitivity,
      -Math.PI * 0.48,
      Math.PI * 0.48,
    );
    this.applyCameraRotation();
  };

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (this.paused) return;
    this.keys.add(event.code);
    if (event.code === "KeyE" && !event.repeat && this.currentTarget) {
      this.interaction.onInteract?.(this.currentTarget);
    }
  };

  private readonly handleKeyUp = (event: KeyboardEvent): void => {
    this.keys.delete(event.code);
  };

  private readonly handleBlur = (): void => {
    this.keys.clear();
    this.velocity.set(0, 0, 0);
  };

  private readonly handleSceneClick = (): void => {
    if (!this.paused && this.requiresPointerLock() && !this.hasPointerLock) void this.resume();
  };

  private requiresPointerLock(): boolean {
    return !hasTouchInput();
  }
}
